<template>
  <ax-dialog 
    ref="dialog"
    title="Export type curves" 
    icon="mdi-export" 
    tooltip="Export type curves"
    size="medium" 
    @submitted="onSubmit"
    @shown="onShow"
  >
    <template slot="content">
      <v-card-text>
        <v-radio-group v-model="exportType" row :rules="rules.required">
          <v-radio v-for="(type, t) in types" :key="t" :label="type.text" :value="type.value" :disabled="type.disabled"></v-radio>
        </v-radio-group>
        <!-- Excel keeps its original single-target/single-method fields.
             ARIES and ComboCurve each get their own block below instead --
             both export straight off the group's declined fit, not a
             free target/method choice. -->
        <template v-if="exportType == 'excel'">
          <v-autocomplete v-model="selectedTarget" :items="targets" label="Target" :rules="rules.required" />
          <v-autocomplete v-model="selectedMethod" :items="methods" label="Method" :rules="rules.required" />
          <v-autocomplete v-model="selectedCurves" multiple :items="curveOptions" label="Type curves" clearable :rules="rules.required" />
          <v-checkbox v-model="averageProduction" label="Include average production" hide-details />
          <!-- <v-checkbox v-model="statistics" label="Include summary statistics" hide-details />
          <v-checkbox v-model="identities" label="Include well identifiers" hide-details /> -->
        </template>
        <template v-if="exportType == 'aries'">
          <v-text-field v-model="tcName"
                        label="Name"
                        clearable
                        :rules="rules.required"
                        :hint="`Becomes the first part of each TYPE_CURVE value, e.g. ${sanitizedNamePreview}_P10_${groupQualifierAbbr}`"
                        persistent-hint />
          <v-autocomplete v-model="selectedTarget" :items="ariesTargetOptions" label="Phase" :rules="rules.required" hint="One phase per export -- only declined targets are shown" persistent-hint />
          <v-text-field :value="groupMethodText" label="Method" disabled hint="The method this group's type curve was actually fit with" persistent-hint />
          <v-text-field :value="groupTechniqueText" label="Technique" disabled persistent-hint />
          <v-autocomplete v-model="selectedCurves" multiple :items="curveOptions" label="Type curves" clearable :rules="rules.required" />
        </template>
        <template v-if="exportType == 'combocurve'">
          <v-text-field v-model="tcName" label="TC Name" clearable hint="Defaults to the group's name followed by TC if left blank" persistent-hint />
          <v-autocomplete v-model="selectedTargets" :items="declinedTargetOptions" label="Targets" multiple :rules="rules.required" hint="Only targets with a declined type curve fit are shown" persistent-hint />
          <v-text-field :value="groupMethodText" label="Method" disabled hint="The method this group's type curve was actually fit with" persistent-hint />
          <v-select v-model="selectedWellIdFormat" :items="wellIdFormats" label="Chosen ID" hint="Identifier listed for the group's wells in Rep Well List Chosen ID" persistent-hint />
          <v-autocomplete v-model="selectedCurves" multiple :items="curveOptions" label="Type curves" clearable :rules="rules.required" />
        </template>
      </v-card-text>
    </template>
  </ax-dialog>
</template>
<script>
import AxDialog from '@/components/dialog'
import { deriveMethodTechniqueAbbr } from '@/utils/aries'
export default {
  name: "dlgExportTypeCurves",
  props: {
    targets: { type: Array, required: true },
    // Targets (values) with a declined type curve fit on the selected group.
    declinedTargets: { type: Array, default: () => [] },
    // Targets whose declined fit also has a Mean curve.
    meanTargets: { type: Array, default: () => [] },
    methods: { type: Array, required: true },
    techniques: { type: Array, default: () => [] },
    // Method/technique the group's type curve was actually fit with --
    // ComboCurve and ARIES show this read-only, not a free choice.
    groupMethod: { type: String, default: null },
    groupTechnique: { type: String, default: null },
    declined: { type: Boolean, default: false },
    terminated: { type: Boolean, default: false }
  },
  components: {
    AxDialog
  },
  data() {
    return {
      types: [
        { text: 'Excel', value: 'excel', disabled: false },
        { text: 'ARIES', value: 'aries', disabled: false },
        { text: 'ComboCurve', value: 'combocurve', disabled: false },
      ],
      curves: ['P10','P50','P90'],
      exportType: 'excel',
      selectedTarget: null,
      selectedMethod: null,
      selectedTargets: [],
      tcName: '',
      wellIdFormats: ['API 10', 'API 14'],
      selectedWellIdFormat: 'API 10',
      selectedCurves: [],
      averageProduction: false,
      // parameters: false,
      // statistics: false,
      // identities: false,
      rules: {}
    }
  },
  computed: {
    declinedTargetOptions() {
      return this.targets.filter(t => this.declinedTargets.includes(t.value))
    },
    // Water excluded for ARIES -- same import-reliability issue as the
    // per-well ARIES exporter.
    ariesTargetOptions() {
      return this.declinedTargetOptions.filter(t => t.value != 'water')
    },
    curveOptions() {
      if (this.exportType == 'excel') return this.curves
      // ARIES picks one target (selectedTarget), ComboCurve several
      // (selectedTargets) -- wrapped in an array so both share one check.
      let targetsInPlay = this.exportType == 'combocurve' ? this.selectedTargets : [this.selectedTarget]
      const hasMean = targetsInPlay.some(t => this.meanTargets.includes(t))
      return hasMean ? [...this.curves, 'Mean'] : this.curves
    },
    groupMethodText() {
      if (!this.groupMethod) return ''
      let m = this.methods.find(m => m.value == this.groupMethod)
      return m ? m.text : this.groupMethod
    },
    groupTechniqueText() {
      if (!this.groupTechnique) return ''
      let t = this.techniques.find(t => t.value == this.groupTechnique)
      return t ? t.text : this.groupTechnique
    },
    groupQualifierAbbr() {
      return deriveMethodTechniqueAbbr(this.groupMethod, this.groupTechnique) || ''
    },
    // Preview only -- onExportForAriesTypeCurve() re-sanitizes at export time.
    sanitizedNamePreview() {
      let s = String(this.tcName || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      return s || 'NAME'
    },
    width() {
      if (this.size == 'small') return 300
      else if (this.size == 'medium') return 600
      else if (this.size == 'large') return 900
      else return null
    },
  },
  watch: {
    // Drop a selected Mean if switching export type/targets makes it unavailable.
    curveOptions(options) {
      this.selectedCurves = this.selectedCurves.filter(c => options.includes(c))
    },
  },
  // watch: {
  //   "exportType": function(val) {
  //     if (!!this.show) {
  //       if (val == 'aries') {
  //         this.statistics = false
  //         this.identities = false
  //       }
  //     }
  //   }
  // },
  methods: {
    onShow() {
      this.rules = {}
      let combocurve = this.types.find(t => t.value == 'combocurve')
      combocurve.disabled = !this.declined
      let aries = this.types.find(t => t.value == 'aries')
      aries.disabled = !this.declined
      this.exportType = 'excel'
      this.selectedTargets = []
      this.tcName = ''
      this.selectedWellIdFormat = 'API 10'
    },
    onSubmit() {
      this.rules = {
        required: [
          v => !!v || 'Required',
          v => !this.isEmpty(v)
        ]
      }
      this.$nextTick(() => {
        //manually trigger Vuetify validation
        if (this.$refs.dialog.$refs.form.validate()) {
          this.$refs.dialog.show = false
          if (this.exportType == 'combocurve') {
            this.$emit('submitted', {
              method: 'combocurve',
              config: {
                targets: this.selectedTargets,
                tc_name: this.tcName,
                well_id_format: this.selectedWellIdFormat,
                curves: this.selectedCurves,
              }
            })
            return
          }
          if (this.exportType == 'aries') {
            this.$emit('submitted', {
              method: 'aries',
              config: {
                name: this.tcName,
                target: this.selectedTarget,
                curves: this.selectedCurves,
              }
            })
            return
          }
          this.$emit('submitted', {
            method: this.exportType,
            config: {
              target: this.selectedTarget,
              method: this.selectedMethod,
              curves: this.selectedCurves,
              production: this.averageProduction,
              // statistics: this.statistics,
              // identities: this.identities
            }
          })
        }
      })
    }
  }
}
</script>